import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import {
    ClientManagerInstanceToken,
    ClientManagerInterface,
    ConfigConstants,
    initialNotificationIdentifierState,
    isDefined,
    LocalStorageDisclaimerService,
    ModalClass,
    NotificationInterface,
    YesNoModalDialogComponent,
    YesNoModalDialogModel,
    ConfigInstanceToken,
    ConfigInterface,
} from '@evorait/pace-core';
import { getDocBox, NotificationInstanceToken } from '@evorait/pace-forms';
import { Store } from '@ngrx/store';
import { BsModalService } from 'ngx-bootstrap/modal';
import { Subject, switchMap } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';
import { navigateToForms } from '../store/actions/protocols.action';
import { AppState } from '../store/state/state';
import { faBars } from '@fortawesome/free-solid-svg-icons';

@Component({
    selector: 'pace-fi-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss'],
    standalone: false,
})
export class HeaderComponent implements OnInit, OnDestroy {
    public isFormsListPage = false;
    public isFormDetailsPage = false;
    public isDocBoxPage = false;
    public appVersion: string;
    private destroy$ = new Subject<void>();
    private chinaDisclaimerId = 'chinaDisclaimerId';
    private chinaDisclaimerText: string;
    public displayDocBox: boolean;
    public isSideMenuOpen = false;
    public faBars = faBars;
    public constructor(
        private readonly router: Router,
        private readonly store: Store<AppState>,
        @Inject(NotificationInstanceToken) private readonly notificationService: NotificationInterface,
        @Inject(ClientManagerInstanceToken) private readonly clientManager: ClientManagerInterface,
        private readonly modalService: BsModalService,
        @Inject(ConfigInstanceToken) private readonly configService: ConfigInterface,
        private readonly localStorageDisclaimerService: LocalStorageDisclaimerService,
    ) {
        this.appVersion = this.clientManager.getAppVersion();
        this.displayDocBox = this.configService.getDisplayDocBox();
        this.chinaDisclaimerText = this.configService.getDisclaimerForChinaFI();
    }

    public ngOnInit(): void {
        this.router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                takeUntil(this.destroy$),
            )
            .subscribe((navEnd: NavigationEnd) => {
                this.isFormsListPage = navEnd.urlAfterRedirects.includes(ConfigConstants.routeUrlForms + '/');
                this.isFormDetailsPage = navEnd.urlAfterRedirects.includes(ConfigConstants.routeUrlForms2 + '/');
                this.isDocBoxPage = navEnd.urlAfterRedirects.includes(ConfigConstants.routeUrlDocBox + '/');
            });
    }

    public openForms(): void {
        if (this.isFormDetailsPage || this.isDocBoxPage) {
            // passing empty identifier as we're always be having 1 notification and returning the same; without depending on passed params
            const notification = this.notificationService.getNotificationStateByIdentifier(
                initialNotificationIdentifierState,
            );

            if (!isDefined(notification)) {
                throw new Error(`No notification available`);
            }
            const qmNum = notification.identifier.qmNum;
            this.store.dispatch(navigateToForms({ orderNumber: qmNum }));
        }
        this.isSideMenuOpen = false;
    }

    public async openDocBox(): Promise<void> {
        const isChinaUser = this.configService.getUserCountryFI() === ConfigConstants.configKeyChina;

        if (this.isFormsListPage || this.isFormDetailsPage) {
            const localDisclaimer = await this.localStorageDisclaimerService.getDisclaimerFromLocalStorageAsync(
                this.chinaDisclaimerId,
            );
            if (isChinaUser && (!localDisclaimer || localDisclaimer !== this.chinaDisclaimerText)) {
                const modalInstance = this.modalService.show(YesNoModalDialogComponent, {
                    backdrop: 'static',
                    class: ModalClass.MODAL_MEDIUM,
                    initialState: {
                        model: new YesNoModalDialogModel(
                            'Disclaimer',
                            this.chinaDisclaimerText,
                            'Confirmed and Continue to DocBox',
                        ),
                    },
                    ariaLabelledBy: 'yes-no-modal-title',
                });

                modalInstance.content?.modalResult
                    .pipe(
                        switchMap(() =>
                            this.localStorageDisclaimerService.saveDisclaimerToLocalStorage(
                                this.chinaDisclaimerId,
                                this.chinaDisclaimerText,
                            ),
                        ),
                        take(1),
                    )
                    .subscribe(() => {
                        this.callDocboxPage();
                    });
            } else {
                this.callDocboxPage();
            }
        }
        this.isSideMenuOpen = false;
    }

    public toggleSideMenu(): void {
        this.isSideMenuOpen = !this.isSideMenuOpen;
    }
    public ngOnDestroy(): void {
        this.destroy$.next();
    }

    private callDocboxPage(): void {
        // passing empty identifier as we're always be having 1 notification and returning the same; without depending on passed params
        const notification = this.notificationService.getNotificationStateByIdentifier(
            initialNotificationIdentifierState,
        );

        if (!isDefined(notification)) {
            throw new Error(`No notification available`);
        }

        this.store.dispatch(
            getDocBox({
                notificationId: notification.identifier,
                materialNumber: notification.equipmentSet[0].matnr,
                serialNumber: notification.equipmentSet[0].sernr,
            }),
        );
    }
}
